import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Modal, Form, Button, Table, Image, Row, Col } from "react-bootstrap";
import {
  buildScopeItems,
  groupKitAddonsByProduct,
  gpmFromPrice,
  priceFromGpm,
} from "../../../../_utils/addScopeItems";
import {
  PRICING_CONTRACT_PRODUCT_LIST_REQUEST,
  PRICING_CONTRACT_LABOR_LIST_REQUEST,
  PRICING_CONTRACT_MATERIAL_LIST_REQUEST,
} from "../../../../_utils/constants/PricingContract";
import { PRODUCT_TYPE_REQUEST } from "../../../../_utils/constants/ProductCatalog";

// Kit rows are keyed by their PARENT product too: two products in the same structure can share the
// same kit (identical materialId/laborId), so without the productId prefix their price/GPM/checkbox
// state would collide and editing one would change the other.
const keyOfAddon = (productId, type, a) =>
  `${productId}-${type}-${a.productId ?? a.materialId ?? a.laborId}`;

// ---------------------------------------------------------------------------
// Presentational body (no Redux) — exported for testing.
//
// Two-phase wizard:
//   1. "select" — a checkbox list of C/C product structures ("What materials would you like to
//      add?"). Pick one or more, then Submit.
//   2. "steps"  — one step per selected structure. Each step loads that structure's products (the
//      parent re-fetches when the body calls onActiveStructureChange) and lets the estimator check
//      products + hand-key each price, plus tick/price the kit items of any kitted product. Each
//      step has Back / Cancel / Submit. Submit on the last step merges every structure's picks and
//      hands { material, labor } (Qty BLANK) back to the parent, which closes the modal.
// ---------------------------------------------------------------------------
export const AddScopeModalBody = ({
  show = true,
  onClose,
  onAdd,
  structureOptions = [],
  products = [],
  activeStructureId = "",
  onActiveStructureChange,
  onProductToggle,
  kitAddonsByProduct = {},
  isLoadingProducts = false,
}) => {
  const [phase, setPhase] = useState("select"); // "select" | "steps"
  const [structChecked, setStructChecked] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [accumulated, setAccumulated] = useState({}); // { [structureId]: { material, labor } }

  // Selection state below is keyed by (globally unique) product/kit ids, so it persists across
  // steps — going Back to a structure re-shows its picks, and each structure's rows are namespaced.
  const [checked, setChecked] = useState({});
  const [productPrices, setProductPrices] = useState({});
  const [productGpms, setProductGpms] = useState({});
  const [kitPrices, setKitPrices] = useState({});
  const [kitGpms, setKitGpms] = useState({});
  const [kitChecked, setKitChecked] = useState({});
  const [priceErrors, setPriceErrors] = useState({ products: {}, kits: {} });

  // The body stays mounted across open/close, so start fresh each time the modal opens —
  // back at the structure picker with no prior selections/prices.
  useEffect(() => {
    if (show) {
      setPhase("select");
      setStructChecked({});
      setSelectedIds([]);
      setStepIndex(0);
      setAccumulated({});
      setChecked({});
      setProductPrices({});
      setProductGpms({});
      setKitPrices({});
      setKitGpms({});
      setKitChecked({});
      setPriceErrors({ products: {}, kits: {} });
    }
  }, [show]);

  // When a structure's products arrive from the pricing-contract API, pre-check and
  // pre-fill price/GPM for any product that already has a contract price. Guard with
  // `prev[id] === undefined` so user edits aren't overwritten when navigating back.
  useEffect(() => {
    if (!Array.isArray(products) || products.length === 0) return;
    setChecked((prev) => {
      const next = { ...prev };
      products.forEach((p) => {
        if (next[p.productId] === undefined && p.price != null && String(p.price).trim() !== "") {
          next[p.productId] = true;
        }
      });
      return next;
    });
    setProductPrices((prev) => {
      const next = { ...prev };
      products.forEach((p) => {
        if (next[p.productId] === undefined && p.price != null && String(p.price).trim() !== "") {
          next[p.productId] = String(p.price);
        }
      });
      return next;
    });
    setProductGpms((prev) => {
      const next = { ...prev };
      products.forEach((p) => {
        if (next[p.productId] === undefined && p.gpm != null && String(p.gpm).trim() !== "") {
          next[p.productId] = String(p.gpm);
        }
      });
      return next;
    });
  }, [products]);

  // When kit addons arrive, pre-check and pre-fill price/GPM for kits that already
  // have a contract price.
  useEffect(() => {
    if (!kitAddonsByProduct || Object.keys(kitAddonsByProduct).length === 0) return;
    const allKitRows = (productId, addons) => [
      ...(addons.materialAddons || []).map((a) => ["material", a]),
      ...(addons.laborAddons || []).map((a) => ["labor", a]),
    ];
    setKitChecked((prev) => {
      const next = { ...prev };
      Object.entries(kitAddonsByProduct).forEach(([productId, addons]) => {
        allKitRows(productId, addons).forEach(([type, a]) => {
          const k = keyOfAddon(productId, type, a);
          if (next[k] === undefined && a.price != null && String(a.price).trim() !== "") {
            next[k] = true;
          }
        });
      });
      return next;
    });
    setKitPrices((prev) => {
      const next = { ...prev };
      Object.entries(kitAddonsByProduct).forEach(([productId, addons]) => {
        allKitRows(productId, addons).forEach(([type, a]) => {
          const k = keyOfAddon(productId, type, a);
          if (next[k] === undefined && a.price != null && String(a.price).trim() !== "") {
            next[k] = String(a.price);
          }
        });
      });
      return next;
    });
    setKitGpms((prev) => {
      const next = { ...prev };
      Object.entries(kitAddonsByProduct).forEach(([productId, addons]) => {
        allKitRows(productId, addons).forEach(([type, a]) => {
          const k = keyOfAddon(productId, type, a);
          if (next[k] === undefined && a.gpm != null && String(a.gpm).trim() !== "") {
            next[k] = String(a.gpm);
          }
        });
      });
      return next;
    });
  }, [kitAddonsByProduct]);

  const toggleProduct = (product, isChecked) => {
    setChecked((prev) => ({ ...prev, [product.productId]: isChecked }));
    if (onProductToggle) onProductToggle(product, isChecked);
  };

  // Price <-> GPM stay in sync via the PricingContract formulas (using each row's currentCost).
  // Editing Price recomputes GPM; editing GPM recomputes Price. Empty input clears both.
  const setPriceGpm = (setPrices, setGpms, id, price, cost) => {
    setPrices((prev) => ({ ...prev, [id]: price }));
    setGpms((prev) => ({ ...prev, [id]: price === "" ? "" : String(gpmFromPrice(price, cost)) }));
  };
  const setGpmPrice = (setPrices, setGpms, id, gpm, cost) => {
    setGpms((prev) => ({ ...prev, [id]: gpm }));
    setPrices((prev) => ({ ...prev, [id]: gpm === "" ? "" : String(priceFromGpm(gpm, cost)) }));
  };

  const checkedProducts = products.filter((p) => checked[p.productId]);
  const hasKitItems = checkedProducts.some((p) => kitAddonsByProduct[p.productId]);

  // A selected item's price must be entered — non-blank, numeric, >= 0.
  const priceError = (raw) => {
    const s = String(raw ?? "").trim();
    if (s === "") return "Price is required";
    const n = Number(s);
    if (!Number.isFinite(n)) return "Price must be a number";
    if (n < 0) return "Price cannot be negative";
    return "";
  };

  // Validate + build the CURRENT structure's picks. Returns either { ok: false, ...errors } or
  // { ok: true, items: { material, labor } }.
  const buildCurrentStructure = () => {
    const productErrors = {};
    const kitErrors = {};

    const productsPayload = checkedProducts.map((p) => ({
      ...p,
      unitPrice: productPrices[p.productId] ?? "",
      gpm: productGpms[p.productId] ?? "",
    }));
    checkedProducts.forEach((p) => {
      const err = priceError(productPrices[p.productId]);
      if (err) productErrors[p.productId] = err;
    });

    const kitMaterials = [];
    const kitLabors = [];
    checkedProducts.forEach((p) => {
      const addons = kitAddonsByProduct[p.productId];
      if (!addons) return;
      (addons.materialAddons || []).forEach((a) => {
        const k = keyOfAddon(p.productId, "material", a);
        if (!kitChecked[k]) return; // only ticked kit items are added
        const err = priceError(kitPrices[k]);
        if (err) kitErrors[k] = err;
        kitMaterials.push({ ...a, productStructureId: p.productStructureId, unitPrice: kitPrices[k] ?? "", gpm: kitGpms[k] ?? "" });
      });
      (addons.laborAddons || []).forEach((a) => {
        const k = keyOfAddon(p.productId, "labor", a);
        if (!kitChecked[k]) return;
        const err = priceError(kitPrices[k]);
        if (err) kitErrors[k] = err;
        kitLabors.push({ ...a, productStructureId: p.productStructureId, unitPrice: kitPrices[k] ?? "", gpm: kitGpms[k] ?? "" });
      });
    });

    if (Object.keys(productErrors).length || Object.keys(kitErrors).length) {
      return { ok: false, productErrors, kitErrors };
    }
    return { ok: true, items: buildScopeItems({ products: productsPayload, kitMaterials, kitLabors }) };
  };

  const labelFor = (id) => structureOptions.find((o) => o.value === id)?.label || "";

  // Phase 1 -> phase 2: fix the ordered list of structures and load the first one.
  const handleStartSteps = () => {
    const ids = structureOptions.filter((o) => structChecked[o.value]).map((o) => o.value);
    if (!ids.length) return;
    setSelectedIds(ids);
    setStepIndex(0);
    setAccumulated({});
    setPriceErrors({ products: {}, kits: {} });
    setPhase("steps");
    if (onActiveStructureChange) onActiveStructureChange(ids[0]);
  };

  const handleStepSubmit = () => {
    const res = buildCurrentStructure();
    if (!res.ok) {
      setPriceErrors({ products: res.productErrors, kits: res.kitErrors });
      return;
    }
    setPriceErrors({ products: {}, kits: {} });

    const structureName = labelFor(activeStructureId);
    const stamped = {
      material: res.items.material.map((item) => ({ ...item, structureName })),
      labor: res.items.labor.map((item) => ({ ...item, structureName })),
    };
    const nextAccum = { ...accumulated, [activeStructureId]: stamped };
    const isLast = stepIndex >= selectedIds.length - 1;
    if (isLast) {
      const merged = { material: [], labor: [] };
      selectedIds.forEach((id) => {
        const it = nextAccum[id];
        if (it) {
          merged.material.push(...it.material);
          merged.labor.push(...it.labor);
        }
      });
      if (onAdd) onAdd(merged);
      return; // parent closes the modal
    }
    setAccumulated(nextAccum);
    const nextIndex = stepIndex + 1;
    setStepIndex(nextIndex);
    if (onActiveStructureChange) onActiveStructureChange(selectedIds[nextIndex]);
  };

  const handleBack = () => {
    setPriceErrors({ products: {}, kits: {} });
    if (stepIndex === 0) {
      setPhase("select"); // back to the structure picker (picks preserved)
      return;
    }
    const prevIndex = stepIndex - 1;
    setStepIndex(prevIndex);
    if (onActiveStructureChange) onActiveStructureChange(selectedIds[prevIndex]);
  };

  const showProductTable = !isLoadingProducts && activeStructureId && products.length > 0;
  const showEmpty = !isLoadingProducts && activeStructureId && products.length === 0;

  return (
    <Modal show={show} size="xl" dialogClassName="add-scope-modal-dialog" onHide={onClose}>
      <style>{`.add-scope-modal-dialog{max-width:95vw;}`}</style>
      <Modal.Header closeButton>
        <Modal.Title>Add Scope</Modal.Title>
      </Modal.Header>

      {phase === "select" ? (
        <>
          <Modal.Body>
            <h6 className="text-center fw-bold mb-4" style={{ color: "#2D831F" }}>
              What materials would you like to add?
            </h6>
            <div style={{ width: "max-content", margin: "0 auto" }}>
              {structureOptions.map((o) => (
                <Form.Check
                  key={o.value}
                  type="checkbox"
                  id={`add-scope-structure-${o.value}`}
                  label={o.label}
                  aria-label={`Select structure ${o.label}`}
                  checked={!!structChecked[o.value]}
                  // A multi-word structure name (e.g. "Cabinet Boxes") must stay on one line — it
                  // shouldn't break mid-name just because the label sits in a max-content column.
                  className="text-nowrap"
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setStructChecked((prev) => ({ ...prev, [o.value]: isChecked }));
                  }}
                />
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer className="justify-content-center">
            <Button
              variant="success"
              type="button"
              onClick={handleStartSteps}
              disabled={structureOptions.every((o) => !structChecked[o.value])}
            >
              Submit
            </Button>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <>
          <Modal.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0" style={{ color: "#2D831F" }}>
                Which {labelFor(activeStructureId)} would you like to add?
              </h6>
              <span className="text-muted small">
                Step {stepIndex + 1} of {selectedIds.length}
              </span>
            </div>

            {isLoadingProducts ? <p className="text-muted mt-3">Loading products…</p> : null}

            {showProductTable ? (
              <Table bordered responsive className="mt-2">
                <thead>
                  <tr>
                    <th style={{ width: "60px" }}>Select</th>
                    {/*<th>Store Name</th>*/}
                    <th>Private Name</th>
                    <th>Manufacturer</th>
                    <th>Style</th>
                    <th>Color</th>
                    <th>Photo</th>
                    <th>Current Cost</th>
                    <th style={{ width: "140px" }}>Price</th>
                    <th style={{ width: "120px" }}>GPM %</th>
                    <th>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, index) => (
                    <tr key={p.productId}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          aria-label={`Select product ${index + 1}`}
                          checked={!!checked[p.productId]}
                          onChange={(e) => toggleProduct(p, e.target.checked)}
                        />
                      </td>
                      {/*<td>{p.storeName}</td>*/}
                      <td>{p.privateName}</td>
                      <td>{p.manufacturer}</td>
                      <td>{p.style}</td>
                      <td>{p.color}</td>
                      <td>
                        {p.photoPath ? <Image width="50" src={p.photoPath} alt={p.privateName || "product"} /> : null}
                      </td>
                      <td>{p.currentCost}</td>
                      <td>
                        <Form.Control
                          type="number"
                          step="0.01"
                          placeholder="Price"
                          aria-label={`Product ${index + 1} price`}
                          value={productPrices[p.productId] ?? ""}
                          isInvalid={!!priceErrors.products[p.productId]}
                          onChange={(e) => {
                            const v = e.target.value;
                            setPriceGpm(setProductPrices, setProductGpms, p.productId, v, p.currentCost);
                          }}
                        />
                        {priceErrors.products[p.productId] ? (
                          <div className="text-danger small mt-1">{priceErrors.products[p.productId]}</div>
                        ) : null}
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          step="0.01"
                          placeholder="GPM %"
                          aria-label={`Product ${index + 1} GPM`}
                          value={productGpms[p.productId] ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setGpmPrice(setProductPrices, setProductGpms, p.productId, v, p.currentCost);
                          }}
                        />
                      </td>
                      <td data-testid={`product-line-total-${p.productId}`}>
                        {String(productPrices[p.productId] ?? "").trim() !== ""
                          ? parseFloat(productPrices[p.productId]).toFixed(2)
                          : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : null}

            {showEmpty ? <p className="text-muted mt-3">No products found for this structure.</p> : null}

            {hasKitItems ? (
              <Row className="mt-3">
                <Col md={12}>
                <h6>Kit Items</h6>
                {checkedProducts.map((p) => {
                  const addons = kitAddonsByProduct[p.productId];
                  if (!addons) return null;
                  const rows = [
                    ...(addons.materialAddons || []).map((a) => ["material", a]),
                    ...(addons.laborAddons || []).map((a) => ["labor", a]),
                  ];
                  return (
                    <div key={p.productId} className="border rounded p-2 mb-2">
                      <strong>{p.privateName || `Product ${p.productId}`}</strong>
                      <Table bordered size="sm" className="mt-2 mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: "50px" }}>Select</th>
                            <th>Name</th>
                            <th style={{ width: "110px" }}>Current Cost</th>
                            <th style={{ width: "140px" }}>Price</th>
                            <th style={{ width: "110px" }}>GPM %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(([type, a]) => {
                            const k = keyOfAddon(p.productId, type, a);
                            const name = type === "labor" ? a.laborName || a.labor : a.privateName || a.productName;
                            return (
                              <tr key={k}>
                                <td>
                                  <Form.Check
                                    type="checkbox"
                                    aria-label={`Select kit ${k}`}
                                    checked={!!kitChecked[k]}
                                    onChange={(e) => {
                                      const isChecked = e.target.checked;
                                      setKitChecked((prev) => ({ ...prev, [k]: isChecked }));
                                    }}
                                  />
                                </td>
                                <td>{name} <span className="flex-grow-1">
                                    {name} <em className="text-muted small">({type})</em>
                                  </span>
                                </td>
                                <td data-testid={`kit-current-cost-${k}`}>{a.currentCost}</td>
                                <td>
                                  <Form.Control
                                    type="number"
                                    step="0.01"
                                    placeholder="Price"
                                    aria-label={`Kit ${k} price`}
                                    value={kitPrices[k] ?? ""}
                                    isInvalid={!!priceErrors.kits[k]}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setPriceGpm(setKitPrices, setKitGpms, k, v, a.currentCost);
                                    }}
                                  />
                                  {priceErrors.kits[k] ? (
                                    <div className="text-danger small mt-1">{priceErrors.kits[k]}</div>
                                  ) : null}
                                </td>
                                <td>
                                  <Form.Control
                                    type="number"
                                    step="0.01"
                                    aria-label={`Kit ${k} GPM`}
                                    placeholder="GPM %"
                                    value={kitGpms[k] ?? ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setGpmPrice(setKitPrices, setKitGpms, k, v, a.currentCost);
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  );
                })}
                </Col>
              </Row>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" type="button" onClick={handleBack}>
              Back
            </Button>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="success" type="button" onClick={handleStepSubmit}>
              Submit
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Connected wrapper — supplies the C/C structure list, and (re)loads the ACTIVE structure's
// products + kit material/labor lists whenever the wizard advances to a new structure step.
// ---------------------------------------------------------------------------
const AddScopeModal = ({
  show,
  onClose,
  onAdd,
  storeId,
  contactInfoId,
  auth: { auth, isAuthenticated },
  ProductType,
  pricingContract,
  fetchProductType,
  fetchProductList,
  fetchLaborList,
  fetchMaterialList,
}) => {
  const token = isAuthenticated && auth?.[0]?.token;
  const [activeStructureId, setActiveStructureId] = useState("");

  // All Countertops/Cabinets product structures — the estimator ticks the ones to add.
  const structureOptions = useMemo(
    () =>
      (Array.isArray(ProductType?.valueList) ? ProductType.valueList : [])
        .filter((v) => v?.isCountertopCabinet === true)
        .map((p) => ({ label: p.productStructure, value: p.productStructureId })),
    [ProductType]
  );

  const products = useMemo(
    () => (Array.isArray(pricingContract?.productList) ? pricingContract.productList : []),
    [pricingContract]
  );

  // Kit relation (matches PricingContract syncMaterial/syncLabor): a product carries a kitId; the
  // structure's material list and labor list rows also carry a kitId. Group each product's kit
  // items by that shared kitId — no per-product async fetch, so no shared-slice race.
  const kitAddonsByProduct = useMemo(
    () =>
      groupKitAddonsByProduct(
        products,
        Array.isArray(pricingContract?.laborList) ? pricingContract.laborList : [],
        Array.isArray(pricingContract?.materialList) ? pricingContract.materialList : []
      ),
    [products, pricingContract]
  );

  // Ensure the structure catalog is available when the modal opens, and clear the active structure
  // so the wizard starts at the structure picker each time.
  useEffect(() => {
    if (show) {
      setActiveStructureId("");
      if (token && !(ProductType?.valueList && ProductType.valueList.length)) {
        fetchProductType(token);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleActiveStructureChange = (structureId) => {
    setActiveStructureId(structureId);
    if (token && structureId) {
      const listParamState = {
        ProductStructureId: structureId,
        StoreId: storeId || 0,
        ContactInfoId: contactInfoId || "",
        IsStock: "",
        IsRecurringStock: "",
        IsPrimary: "",
        IsSecondary: "",
        PrivateName: "",
        Manufacturer: "",
        Style: "",
        Color: "",
        Fiber: "",
        faceWidth: "",
        BackingType: "",
        Guage: "",
        WearLayer: "",
        Thickness: "",
        currentCost: "",
        futureCost1: "",
        futureCost2: "",
        grpPricingContractId: "",
        subProductStructureId: 0,
      };
      // Products + the structure's kit material/labor lists (kit items are matched by kitId).
      fetchProductList(token, listParamState);
      fetchLaborList(token, listParamState);
      fetchMaterialList(token, listParamState);
    }
  };

  return (
    <AddScopeModalBody
      show={show}
      onClose={onClose}
      onAdd={onAdd}
      structureOptions={structureOptions}
      products={products}
      activeStructureId={activeStructureId}
      onActiveStructureChange={handleActiveStructureChange}
      kitAddonsByProduct={kitAddonsByProduct}
      isLoadingProducts={Boolean(pricingContract?.isLoading)}
    />
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  ProductType: state.ProductType,
  pricingContract: state.pricingContract,
});

const mapDispatchToProps = (dispatch) => ({
  fetchProductType: (token) => dispatch({ type: PRODUCT_TYPE_REQUEST, token }),
  fetchProductList: (token, listParamState) =>
    dispatch({ type: PRICING_CONTRACT_PRODUCT_LIST_REQUEST, token, listParamState, checkFlag: false }),
  fetchLaborList: (token, listParamState) =>
    dispatch({ type: PRICING_CONTRACT_LABOR_LIST_REQUEST, token, listParamState, checkFlag: false }),
  fetchMaterialList: (token, listParamState) =>
    dispatch({ type: PRICING_CONTRACT_MATERIAL_LIST_REQUEST, token, listParamState, checkFlag: false }),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddScopeModal);
